import { useQuery } from '@tanstack/react-query';
import { fetchSecurityIncidents } from '@/lib/api';
import { Link } from 'react-router-dom';
import { StatusDot } from '@/components/shared/StatusDot';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Shield, AlertTriangle, Lock, Eye } from 'lucide-react';

const SEVERITY_STYLES = {
  CRITICAL: 'bg-red-500/15 text-red-300 border-red-500/30',
  HIGH: 'bg-amber-500/15 text-amber-200 border-amber-500/30',
  MEDIUM: 'bg-indigo-500/15 text-indigo-200 border-indigo-500/30',
  LOW: 'bg-slate-500/15 text-slate-200 border-slate-500/30',
};

export default function SecurityPage() {
  const { data: incidents, isLoading } = useQuery({
    queryKey: ['security-incidents'],
    queryFn: fetchSecurityIncidents,
    refetchInterval: 15000,
  });

  return (
    <div data-testid="security-page" className="w-full max-w-[1600px] mx-auto">
      <div className="mb-4">
        <h1 className="font-heading text-xl font-semibold text-[var(--nexus-text-primary)] tracking-tight">Security Incidents</h1>
        <p className="text-xs text-[var(--nexus-text-muted)] font-mono mt-0.5">SENTINL agent: threat detection, containment, evidence collection • {(incidents || []).length} incidents</p>
      </div>

      {isLoading ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-24 rounded-xl bg-[var(--nexus-bg-elevated)] animate-pulse" />)}</div>
      ) : (
        <div className="space-y-3" data-testid="security-list">
          {(incidents || []).map(inc => {
            const sevStyle = SEVERITY_STYLES[inc.priority === 'P1' ? 'CRITICAL' : inc.priority === 'P2' ? 'HIGH' : 'MEDIUM'] || SEVERITY_STYLES.MEDIUM;
            return (
              <div key={inc.id} className="nexus-card relative overflow-hidden p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-[var(--nexus-danger)] bg-opacity-10">
                      <Shield size={18} className="text-[var(--nexus-danger)]" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <Link to={`/incidents/${inc.id}`} className="font-mono text-xs text-[var(--nexus-primary-bright)] hover:underline">{inc.id}</Link>
                        <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded border ${sevStyle}`}>{inc.priority}</span>
                        <StatusDot status={inc.status === 'RESOLVED' ? 'success' : inc.status === 'IN_PROGRESS' ? 'processing' : 'danger'} pulse={inc.status !== 'RESOLVED'} size="sm" />
                        <span className="text-[10px] text-[var(--nexus-text-muted)]">{inc.status}</span>
                      </div>
                      <p className="text-sm text-[var(--nexus-text-primary)] mt-1">{inc.title}</p>
                      {inc.root_cause && (
                        <div className="mt-2 px-2 py-1 rounded bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)]">
                          <span className="text-[10px] font-mono text-[var(--nexus-danger)]">{inc.root_cause}</span>
                        </div>
                      )}
                      {inc.resolution && (
                        <div className="mt-1 px-2 py-1 rounded bg-green-500/5 border border-green-500/10">
                          <span className="text-[10px] text-[var(--nexus-success)]">{inc.resolution}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-3 mt-2">
                        <span className="font-mono text-[10px] text-[var(--nexus-accent)]">{inc.assigned_agent}</span>
                        {inc.affected_cis?.length > 0 && (
                          <span className="text-[10px] text-[var(--nexus-text-muted)]">{inc.affected_cis.join(', ')}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  {inc.confidence > 0 && (
                    <div className="text-right">
                      <span className="font-mono text-lg font-semibold text-[var(--nexus-danger)] tabular-nums">{inc.confidence}%</span>
                      <p className="text-[9px] text-[var(--nexus-text-muted)]">severity</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {(!incidents || incidents.length === 0) && (
            <div className="nexus-card p-12 text-center">
              <Shield size={32} className="text-[var(--nexus-text-muted)] mx-auto mb-3" />
              <p className="text-sm text-[var(--nexus-text-muted)]">No security incidents</p>
              <p className="text-[10px] text-[var(--nexus-text-muted)] mt-1">Trigger a SENTINL scenario from Settings to simulate a security event</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
